// app tsx
import React, { useState, useRef, useEffect } from 'react';
import ImageUploader from './ImageUploader';
import SpherePreviewer from './SpherePreviewer';
import ProjectionPreviewer from './ProjectionPreviewer';
import EditableLabel from './EditableLabel';
import { handleInputChangeWithSnap, formatExportFilename } from './utils';
import { 
  MAX_LATITUDE, 
  MAX_LONGITUDE, 
  PROJECTION_TYPES, 
  FILE_FORMATS, 
  DEFAULT_PETALS, 
  DEFAULT_SURPLUSY, 
  DEFAULT_SURPLUSX, 
  DEFAULT_MERIDIAN, 
  DEFAULT_START_SURPLUSX, 
  DEFAULT_END_SURPLUSX, 
  DEFAULT_START_SURPLUSY, 
  DEFAULT_END_SURPLUSY,
  DEFAULT_BEELANDING,
  MAX_BEELANDING,
  MIN_BEELANDING
} from './constants';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import * as THREE from 'three';
import { ArrowDownToLine, RotateCcw, Upload } from 'lucide-react';
import ConsoleLogger from './ConsoleLogger';
import ControlBar from './ControlBar';

// Add global styles for range inputs
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 32px;
    background: #1a1a1a;
    border-radius: 16px;
    outline: none;
    border: none;
    cursor: pointer;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
  }

  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 32px;
    height: 32px;
    background: #1a1a1a;
    border: 2px solid #00a000;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    cursor: grab;
    margin-top: 0;
    transition: all 0.2s ease;
    opacity: 0.62;
  }

  input[type="range"]::-moz-range-thumb {
    width: 24px;
    height: 24px;
    background: #1a1a1a;
    border: 2px solid #00a000;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    cursor: grab;
    transition: all 0.2s ease;
    opacity: 0.62;
  }

  input[type="range"]::-moz-range-track {
    background: #1a1a1a;
    height: 32px;
    border-radius: 16px;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
  }

  input[type="range"]:hover::-webkit-slider-thumb {
    background: #222222;
    border-color: #00c000;
    transform: scale(1.1);
    opacity: 1;
  }

  input[type="range"]:hover::-moz-range-thumb {
    background: #222222;
    border-color: #00c000;
    transform: scale(1.1);
    opacity: 1;
  }

  input[type="range"]:active::-webkit-slider-thumb {
    cursor: grabbing;
    background: #00a000;
    border-color: #00a000;
  }

  input[type="range"]:active::-moz-range-thumb {
    cursor: grabbing;
    background: #00a000;
    border-color: #00a000;
  }

  input[type="range"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  input[type="range"]:disabled::-webkit-slider-thumb {
    background: #1a1a1a;
    border-color: #333333;
    cursor: not-allowed;
  }

  input[type="range"]:disabled::-moz-range-thumb {
    background: #1a1a1a;
    border-color: #333333;
    cursor: not-allowed;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(styleSheet);

const App: React.FC = () => {
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [fileSize, setFileSize] = useState<string>("");
  const [imageResolution, setImageResolution] = useState<string>("");
  const [imageWidth, setImageWidth] = useState(0);
  const [tilt, setTilt] = useState(0);
  const [isRotating, setIsRotating] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [pinch, setPinch] = useState(0);
  const [trim, setTrim] = useState(0);
  const [selectedProjection, setSelectedProjection] = useState<keyof typeof PROJECTION_TYPES>('EQUIRECTANGULAR');
  const [offset, setOffset] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [meridian, setMeridian] = useState<number>(DEFAULT_MERIDIAN);
  const [petals, setPetals] = useState<number>(DEFAULT_PETALS);
  const [surplusY, setSurplusY] = useState<number>(DEFAULT_SURPLUSY);
  const [surplusX, setSurplusX] = useState<number>(DEFAULT_SURPLUSX);
  const [beeLanding, setBeeLanding] = useState<number>(DEFAULT_BEELANDING);
  const [spin, setSpin] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isGlbPressed, setIsGlbPressed] = useState(false);
  const [isGltfPressed, setIsGltfPressed] = useState(false);
  const [isWebmPressed, setIsWebmPressed] = useState(false);
  const [isJpgPressed, setIsJpgPressed] = useState(false);
  const [isPngPressed, setIsPngPressed] = useState(false);
  const [isWebpPressed, setIsWebpPressed] = useState(false);
  const [isResetPressed, setIsResetPressed] = useState(false);
  const [isExportingVideo, setIsExportingVideo] = useState(false);
  const [previousRotationState, setPreviousRotationState] = useState<boolean>(false);
  
  const spherePreviewerRef = useRef<{
    recordVideo: (onComplete?: (frameCount: number) => void) => void;
    getScene: () => THREE.Scene;
    exportModel: (binary: boolean) => void;
    captureSnapshot: () => Promise<string>;
}>(null);

  useEffect(() => {
    console.log('Input image changed:', inputImage);
  }, [inputImage]);

  useEffect(() => {
    // Cleanup function for the image URL
    return () => {
      if (inputImage) {
        console.log('Cleaning up URL:', inputImage);
        URL.revokeObjectURL(inputImage);
      }
    };
  }, []); // Empty dependency array - only cleanup on unmount

  const formatFileName = (name: string): string => {
    if (name.length <= 26) return name;
    
    const lastDotIndex = name.lastIndexOf('.');
    if (lastDotIndex === -1) return name.slice(0, 23) + '...';
    
    const extension = name.slice(lastDotIndex);
    const baseLength = 23 - extension.length;
    return name.slice(0, baseLength) + '...' + extension;
  };

  const handleImageUpload = (file: File) => {
    if (!file) {
      console.log('No files selected');
      return;
    }
  
    // Check file size (42MB limit)
    const MAX_SIZE = 42 * 1024 * 1024; // 42MB in bytes
    if (file.size > MAX_SIZE) {
      console.error('File size exceeds 42MB limit');
      // Clear previous image and its data
      setInputImage(null);
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      setFileName(formatFileName(file.name));
      setFileSize(`${fileSizeMB}MB`);
      setImageResolution(`${fileSizeMB}MB exceeds 42MB limit`);
      return;
    }
  
    try {
      if (inputImage) {
        URL.revokeObjectURL(inputImage);
      }
      
      const imageUrl = URL.createObjectURL(file);
      setInputImage(imageUrl);
      setFileName(formatFileName(file.name));
      setFileSize(`${(file.size / 1024 / 1024).toFixed(2)}MB`);
      // Reset rotation state to true for new valid images
      setIsRotating(true);
      
      const img = new Image();
      img.onload = () => {
        setImageResolution(`${img.width}x${img.height}`);
        setImageWidth(img.width);
      };
      img.onerror = () => {
        console.error('Error loading image');
        URL.revokeObjectURL(imageUrl);
        setInputImage(null);
      };
      img.src = imageUrl;
      
      addToHistory({ type: 'imageUpload', value: imageUrl });
    } catch (error) {
      console.error('Error handling image upload:', error);
      setInputImage(null);
    }
  };

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<number>>, value: string, max: number, type: string) => {
    const numValue = Math.min(Math.max(-max, Number(value)), max);
    setter(numValue);
    addToHistory({ type, value: numValue });
  };

  const addToHistory = (action: any) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(action);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      applyHistoryState(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      applyHistoryState(history[historyIndex]);
    }
  };

  const applyHistoryState = (state: any) => {
    console.log('Applying state:', state);
  };

  const exportModel = async (binary: boolean): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!spherePreviewerRef.current) {
        console.error('Sphere previewer ref is not available');
        reject(new Error('Sphere previewer not available'));
        return;
      }
  
      const scene = spherePreviewerRef.current.getScene();
      const exporter = new GLTFExporter();
  
      const clonedScene = scene.clone();
      const sphereMesh = clonedScene.children.find(child => child instanceof THREE.Mesh) as THREE.Mesh;
  
      if (sphereMesh && sphereMesh.material instanceof THREE.ShaderMaterial) {
        const newMaterial = new THREE.MeshStandardMaterial();
        const texture = sphereMesh.material.uniforms.uTexture.value;
        newMaterial.map = texture;
        sphereMesh.material = newMaterial;
      }
  
      // Use FILE_FORMATS from constants
      const format = binary ? FILE_FORMATS.GLB : FILE_FORMATS.GLTF;
      const exportFilename = formatExportFilename(fileName, 'SPHERE', format);
  
      exporter.parse(
        clonedScene,
        (gltf) => {
          let blob: Blob;
          if (binary) {
            blob = new Blob([gltf as ArrayBuffer], { type: 'application/octet-stream' });
          } else {
            const output = JSON.stringify(gltf, null, 2);
            blob = new Blob([output], { type: 'text/plain' });
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = exportFilename;
          link.click();
          URL.revokeObjectURL(url);
          resolve();
        },
        (error) => {
          console.error('An error happened during export:', error);
          reject(error);
        },
        { binary: binary }
      );
    });
  };
  
// Previous exportModel function remains the same

const exportWebMVideo = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!spherePreviewerRef.current) {
      console.error('Sphere previewer ref is not available');
      reject(new Error('Sphere previewer not available'));
      return;
    }

    const exportFilename = formatExportFilename(fileName, 'SPHERE', FILE_FORMATS.WEBM);
    localStorage.setItem('exportFilename', exportFilename);
    
    spherePreviewerRef.current.recordVideo();
    resolve();
  });
};

const exportProjection = async (format: keyof typeof FILE_FORMATS): Promise<void> => {
  return new Promise((resolve, reject) => {
    const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas not found');
      reject(new Error('Canvas not found'));
      return;
    }

    try {
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
      if (!gl) {
        throw new Error('WebGL context not found');
      }

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      const width = canvas.width;
      const height = canvas.height;
      if (width === 0 || height === 0) {
        throw new Error('Invalid canvas dimensions');
      }

      const pixels = new Uint8Array(width * height * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

      const offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      const ctx = offscreen.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get 2D context');
      }

      if (format === 'JPEG') {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
      }

      const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        throw new Error('Failed to get temp context');
      }

      tempCtx.putImageData(imageData, 0, 0);

      ctx.translate(0, height);
      ctx.scale(1, -1);
      ctx.drawImage(tempCanvas, 0, 0);

      const exportQuality = format === 'JPEG' ? 0.95 : undefined;
      
      // Use formatExportFilename for consistent naming
      const exportFilename = formatExportFilename(
        fileName,
        selectedProjection,
        FILE_FORMATS[format]
      );
      
      offscreen.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = exportFilename;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        },
        `image/${FILE_FORMATS[format]}`,
        exportQuality
      );
    } catch (error) {
      reject(error);
    }
  });
};

const exportHalfProjection = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas not found');
      reject(new Error('Canvas not found'));
      return;
    }

    try {
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
      if (!gl) {
        throw new Error('WebGL context not found');
      }

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      const width = canvas.width;
      const height = canvas.height;
      const halfWidth = Math.floor(width / 2);
      
      if (halfWidth === 0 || height === 0) {
        throw new Error('Invalid canvas dimensions');
      }

      const pixels = new Uint8Array(halfWidth * height * 4);
      gl.readPixels(0, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

      // Find content boundaries
      let minX = halfWidth;
      let maxX = 0;
      let minY = height;
      let maxY = 0;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < halfWidth; x++) {
          const idx = (y * halfWidth + x) * 4;
          const alpha = pixels[idx + 3];
          
          if (alpha > 0) {  // If pixel is not fully transparent
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
          }
        }
      }

      const padding = 1;
      minX = Math.max(0, minX - padding);
      maxX = Math.min(halfWidth - 1, maxX + padding);
      minY = Math.max(0, minY - padding);
      maxY = Math.min(height - 1, maxY + padding);

      const trimmedWidth = maxX - minX + 1;
      const trimmedHeight = maxY - minY + 1;

      const trimmedPixels = new Uint8Array(trimmedWidth * trimmedHeight * 4);
      
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const srcIdx = (y * halfWidth + x) * 4;
          const dstIdx = ((y - minY) * trimmedWidth + (x - minX)) * 4;
          trimmedPixels[dstIdx] = pixels[srcIdx];
          trimmedPixels[dstIdx + 1] = pixels[srcIdx + 1];
          trimmedPixels[dstIdx + 2] = pixels[srcIdx + 2];
          trimmedPixels[dstIdx + 3] = pixels[srcIdx + 3];
        }
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = trimmedWidth;
      offscreen.height = trimmedHeight;
      const ctx = offscreen.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get 2D context');
      }

      const imageData = new ImageData(new Uint8ClampedArray(trimmedPixels), trimmedWidth, trimmedHeight);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = trimmedWidth;
      tempCanvas.height = trimmedHeight;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        throw new Error('Failed to get temp context');
      }

      tempCtx.putImageData(imageData, 0, 0);
      ctx.translate(0, trimmedHeight);
      ctx.scale(1, -1);
      ctx.drawImage(tempCanvas, 0, 0);

      // Use formatExportFilename with isHalf parameter
      const exportFilename = formatExportFilename(
        fileName,
        selectedProjection,
        FILE_FORMATS.PNG,
        true // isHalf parameter
      );

      offscreen.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = exportFilename;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        },
        `image/${FILE_FORMATS.PNG}` // Use FILE_FORMATS constant
      );
    } catch (error) {
      reject(error);
    }
  });
};

  
  const resetProjectionControls = () => {
    setOffset(0);
    setLatitude(0);
    setLongitude(0);
    setSpin(0);
    setPinch(0);
    setTrim(0);
    setPetals(4);
    setBeeLanding(DEFAULT_BEELANDING);
    setSurplusY(90);
    setSurplusX(2);
    setMeridian(0);
  };
  
  const handleGlbClick = async () => {
    setIsGlbPressed(true);
    await exportModel(true);
    setTimeout(() => setIsGlbPressed(false), 260);
  };

  const handleGltfClick = async () => {
    setIsGltfPressed(true);
    await exportModel(false);
    setTimeout(() => setIsGltfPressed(false), 260);
  };
  
  const handleWebmClick = async () => {
    setIsWebmPressed(true);
    setIsExportingVideo(true);
    
    // Store the current rotation state before starting
    const wasRotating = isRotating;
    
    // Force rotation on for the video
    setIsRotating(true);
  
    try {
      await exportWebMVideo();
    } finally {
      // Delay resetting the states until after the video is complete
      setTimeout(() => {
        // Restore the original rotation state
        setIsRotating(wasRotating);
        setIsWebmPressed(false);
        setIsExportingVideo(false);
      }, 11000);
    }
  };
  
  const handleSphereSnapshot = async () => {  // Changed from handleGridClick
    if (!spherePreviewerRef.current) {
      console.error('Sphere previewer not available');
      return;
    }
  
    try {
      // Get single snapshot without affecting rotation
      const snapshotDataUrl = await spherePreviewerRef.current.captureSnapshot();
  
      // Create download link with PNG extension
      const exportFilename = formatExportFilename(fileName, 'SPHERE', 'PNG');
      const link = document.createElement('a');
      link.download = exportFilename;
      link.href = snapshotDataUrl;
      link.click();
  
    } catch (error) {
      console.error('Failed to capture sphere snapshot:', error);
    }
  };
  
  const handlePngClick = async () => {
    setIsPngPressed(true);
    await exportProjection('PNG');
    setTimeout(() => setIsPngPressed(false), 260);
  };

  const handleHalfPngClick = async () => {
    setIsPngPressed(true);
    await exportHalfProjection();
    setTimeout(() => setIsPngPressed(false), 260);
  };

  const handleJpgClick = async () => {
    setIsJpgPressed(true);
    await exportProjection('JPEG');
    setTimeout(() => setIsJpgPressed(false), 260);
  };
  
  const handleWebpClick = async () => {
    setIsWebpPressed(true);
    await exportProjection('WEBP');
    setTimeout(() => setIsWebpPressed(false), 260);
  };

  const handleResetClick = () => {
    setIsResetPressed(true);
    resetProjectionControls();
    setTimeout(() => setIsResetPressed(false), 260);
  };


  const handlePeelClick = async () => {
    const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas not found');
      return;
    }
  
    try {
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
      if (!gl) {
        throw new Error('WebGL context not found');
      }
  
      // Initial setup
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
  
      const width = canvas.width;
      const height = canvas.height;
      const halfWidth = Math.floor(width / 2);
      const halfHeight = Math.floor(height / 2);
  
      // Read pixels from both halves
      const leftPixels = new Uint8Array(halfWidth * height * 4);
      const rightPixels = new Uint8Array(halfWidth * height * 4);
      gl.readPixels(0, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, leftPixels);
      gl.readPixels(halfWidth, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, rightPixels);
  
      // Create source canvases for both halves
      const leftCanvas = document.createElement('canvas');
      const rightCanvas = document.createElement('canvas');
      [leftCanvas, rightCanvas].forEach(canvas => {
        canvas.width = halfWidth;
        canvas.height = height;
      });
  
      // Setup contexts with alpha channel enabled
      const leftCtx = leftCanvas.getContext('2d', { alpha: true })!;
      const rightCtx = rightCanvas.getContext('2d', { alpha: true })!;
      leftCtx.putImageData(new ImageData(new Uint8ClampedArray(leftPixels), halfWidth, height), 0, 0);
      rightCtx.putImageData(new ImageData(new Uint8ClampedArray(rightPixels), halfWidth, height), 0, 0);
  
      const extractBanana = (
        sourceCanvas: HTMLCanvasElement,
        yStart: number,
        isUpperTriangle: boolean,
        rotationAngle: number,
        isRightSide: boolean,
        needsFlip: boolean = false
      ) => {
        const segmentCanvas = document.createElement('canvas');
        segmentCanvas.width = halfWidth;
        segmentCanvas.height = halfHeight;
        const segCtx = segmentCanvas.getContext('2d', { alpha: true })!;
  
        // Set up clipping region
        segCtx.save();
        segCtx.beginPath();
        if (isUpperTriangle) {
          if (!isRightSide) {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(halfWidth, 0);
            segCtx.lineTo(0, halfHeight);
          } else {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(halfWidth, 0);
            segCtx.lineTo(halfWidth, halfHeight);
          }
        } else {
          if (!isRightSide) {
            segCtx.moveTo(halfWidth, 0);
            segCtx.lineTo(halfWidth, halfHeight);
            segCtx.lineTo(0, halfHeight);
          } else {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(0, halfHeight);
            segCtx.lineTo(halfWidth, halfHeight);
          }
        }
        segCtx.closePath();
        segCtx.clip();
  
        // Draw with y-flip correction
        segCtx.scale(1, -1);
        segCtx.translate(0, -halfHeight);
        segCtx.drawImage(sourceCanvas,
          0, height - yStart - halfHeight,
          halfWidth, halfHeight,
          0, 0,
          halfWidth, halfHeight
        );
        segCtx.restore();
  
        // Create final rotated version
        const finalCanvas = document.createElement('canvas');
        const size = Math.max(halfWidth, halfHeight) * 2;
        finalCanvas.width = size;
        finalCanvas.height = size;
        const finalCtx = finalCanvas.getContext('2d', { alpha: true })!;
  
        // Center and rotate
        finalCtx.translate(size/2, size/2);
        finalCtx.rotate(rotationAngle * Math.PI / 180);
        finalCtx.drawImage(segmentCanvas, -halfWidth/2, -halfHeight/2);
  
        // Get and apply content bounds
        const imageData = finalCtx.getImageData(0, 0, size, size);
        const bounds = getContentBounds(imageData);
  
        const trimmedCanvas = document.createElement('canvas');
        trimmedCanvas.width = bounds.width;
        trimmedCanvas.height = bounds.height;
        const trimCtx = trimmedCanvas.getContext('2d', { alpha: true })!;
  
        // Draw the banana with potential 180° rotation
        trimCtx.save();
        if (needsFlip) {
          trimCtx.translate(bounds.width/2, bounds.height/2);
          trimCtx.rotate(Math.PI);
          trimCtx.translate(-bounds.width/2, -bounds.height/2);
        }
        
        trimCtx.drawImage(finalCanvas, 
          bounds.left, bounds.top, bounds.width, bounds.height,
          0, 0, bounds.width, bounds.height
        );
        
        trimCtx.restore();
  
        return trimmedCanvas;
      };
  
      const getContentBounds = (imageData: ImageData) => {
        const { width, height, data } = imageData;
        let minX = width, minY = height, maxX = 0, maxY = 0;
        
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const alpha = data[i + 3];
            if (alpha > 0) {
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }
        }
        
        return {
          left: minX,
          top: minY,
          width: maxX - minX + 1,
          height: maxY - minY + 1
        };
      };
  
      // Extract all bananas with 180° rotation for 1,4,5,8
      const banana5 = extractBanana(leftCanvas, 0, true, 45, false, true);
      const banana1 = extractBanana(leftCanvas, 0, false, 225, false, true);
      const banana6 = extractBanana(leftCanvas, halfHeight, true, 45, false);
      const banana2 = extractBanana(leftCanvas, halfHeight, false, 225, false);
  
      const banana3 = extractBanana(rightCanvas, 0, true, 315, true);
      const banana7 = extractBanana(rightCanvas, 0, false, 135, true);
      const banana8 = extractBanana(rightCanvas, halfHeight, true, 315, true, true);
      const banana4 = extractBanana(rightCanvas, halfHeight, false, 135, true, true);
  
      // Combine all bananas in the correct order
      const bananas = [banana1, banana2, banana3, banana4, banana5, banana6, banana7, banana8];
      const maxWidth = Math.max(...bananas.map(b => b.width));
      const maxHeight = Math.max(...bananas.map(b => b.height));
      const interBananaPadding = 5; // Padding between bananas
      const borderPadding = 3;     // Padding around the entire composition
  
      // Create final composition with all 8 bananas plus padding
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = maxWidth * 8 + interBananaPadding * 7 + borderPadding * 2;
      finalCanvas.height = maxHeight + borderPadding * 2;
      
      const finalCtx = finalCanvas.getContext('2d', { alpha: true })!;
      // Remove the white background fill
      finalCtx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
  
      // Place all bananas with padding
      bananas.forEach((banana, index) => {
        const x = borderPadding + index * (maxWidth + interBananaPadding);
        const y = borderPadding + (maxHeight - banana.height) / 2;
        finalCtx.drawImage(banana, x, y);
      });
  
      // Export
      const exportFilename = formatExportFilename(
        fileName,
        'BASKETBALL_PEEL',
        FILE_FORMATS.PNG
      );
  
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to create blob');
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = exportFilename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
  
    } catch (error) {
      console.error('Failed to export peel:', error);
    }
  };


  const handleBananasClick = async () => {
    const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas not found');
      return;
    }
  
    try {
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
      if (!gl) {
        throw new Error('WebGL context not found');
      }
  
      // Initial setup
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
  
      const width = canvas.width;
      const height = canvas.height;
      const halfWidth = Math.floor(width / 2);
      const halfHeight = Math.floor(height / 2);
  
      // Read pixels from both halves
      const leftPixels = new Uint8Array(halfWidth * height * 4);
      const rightPixels = new Uint8Array(halfWidth * height * 4);
      gl.readPixels(0, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, leftPixels);
      gl.readPixels(halfWidth, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, rightPixels);
  
      // Create source canvases for both halves
      const leftCanvas = document.createElement('canvas');
      const rightCanvas = document.createElement('canvas');
      [leftCanvas, rightCanvas].forEach(canvas => {
        canvas.width = halfWidth;
        canvas.height = height;
      });
  
      // Setup contexts with alpha channel enabled
      const leftCtx = leftCanvas.getContext('2d', { alpha: true })!;
      const rightCtx = rightCanvas.getContext('2d', { alpha: true })!;
      leftCtx.putImageData(new ImageData(new Uint8ClampedArray(leftPixels), halfWidth, height), 0, 0);
      rightCtx.putImageData(new ImageData(new Uint8ClampedArray(rightPixels), halfWidth, height), 0, 0);
  
      const extractBanana = (
        sourceCanvas: HTMLCanvasElement,
        yStart: number,
        isUpperTriangle: boolean,
        rotationAngle: number,
        isRightSide: boolean,
        needsFlip: boolean = false
      ) => {
        const segmentCanvas = document.createElement('canvas');
        segmentCanvas.width = halfWidth;
        segmentCanvas.height = halfHeight;
        const segCtx = segmentCanvas.getContext('2d', { alpha: true })!;
  
        // Set up clipping region
        segCtx.save();
        segCtx.beginPath();
        if (isUpperTriangle) {
          if (!isRightSide) {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(halfWidth, 0);
            segCtx.lineTo(0, halfHeight);
          } else {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(halfWidth, 0);
            segCtx.lineTo(halfWidth, halfHeight);
          }
        } else {
          if (!isRightSide) {
            segCtx.moveTo(halfWidth, 0);
            segCtx.lineTo(halfWidth, halfHeight);
            segCtx.lineTo(0, halfHeight);
          } else {
            segCtx.moveTo(0, 0);
            segCtx.lineTo(0, halfHeight);
            segCtx.lineTo(halfWidth, halfHeight);
          }
        }
        segCtx.closePath();
        segCtx.clip();
  
        // Draw with y-flip correction
        segCtx.scale(1, -1);
        segCtx.translate(0, -halfHeight);
        segCtx.drawImage(
          sourceCanvas,
          0,
          height - yStart - halfHeight,
          halfWidth,
          halfHeight,
          0,
          0,
          halfWidth,
          halfHeight
        );
        segCtx.restore();
  
        // Create final rotated version
        const finalCanvas = document.createElement('canvas');
        const size = Math.max(halfWidth, halfHeight) * 2;
        finalCanvas.width = size;
        finalCanvas.height = size;
        const finalCtx = finalCanvas.getContext('2d', { alpha: true })!;
  
        // Center and rotate
        finalCtx.translate(size / 2, size / 2);
        finalCtx.rotate((rotationAngle * Math.PI) / 180);
        finalCtx.drawImage(segmentCanvas, -halfWidth / 2, -halfHeight / 2);
  
        // Get and apply content bounds
        const imageData = finalCtx.getImageData(0, 0, size, size);
        const bounds = getContentBounds(imageData);
  
        const trimmedCanvas = document.createElement('canvas');
        trimmedCanvas.width = bounds.width;
        trimmedCanvas.height = bounds.height;
        const trimCtx = trimmedCanvas.getContext('2d', { alpha: true })!;
  
        // Draw the banana with potential 180° rotation if needed
        trimCtx.save();
        if (needsFlip) {
          trimCtx.translate(bounds.width / 2, bounds.height / 2);
          trimCtx.rotate(Math.PI);
          trimCtx.translate(-bounds.width / 2, -bounds.height / 2);
        }
        trimCtx.drawImage(
          finalCanvas,
          bounds.left,
          bounds.top,
          bounds.width,
          bounds.height,
          0,
          0,
          bounds.width,
          bounds.height
        );
        trimCtx.restore();
  
        return trimmedCanvas;
      };
  
      const getContentBounds = (imageData: ImageData) => {
        const { width, height, data } = imageData;
        let minX = width,
          minY = height,
          maxX = 0,
          maxY = 0;
  
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const alpha = data[i + 3];
            if (alpha > 0) {
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }
        }
  
        return {
          left: minX,
          top: minY,
          width: maxX - minX + 1,
          height: maxY - minY + 1,
        };
      };
  
      // Extract bananas with the new export order: 1-2-5-6-3-4-7-8
      // Bananas 1, 5, 4, and 8 now have the flip flag set to false.
      const banana1 = extractBanana(leftCanvas, 0, false, 225, false, false);
      const banana2 = extractBanana(leftCanvas, halfHeight, false, 225, false);
      const banana5 = extractBanana(leftCanvas, 0, true, 45, false, false);
      const banana6 = extractBanana(leftCanvas, halfHeight, true, 45, false);
  
      const banana3 = extractBanana(rightCanvas, 0, true, 315, true);
      const banana4 = extractBanana(rightCanvas, halfHeight, false, 135, true, false);
      const banana7 = extractBanana(rightCanvas, 0, false, 135, true);
      const banana8 = extractBanana(rightCanvas, halfHeight, true, 315, true, false);
  
      // Combine all bananas in the new correct order: 1-2-5-6-3-4-7-8
      const bananas = [banana1, banana2, banana5, banana6, banana3, banana4, banana7, banana8];
      const maxWidth = Math.max(...bananas.map(b => b.width));
      const maxHeight = Math.max(...bananas.map(b => b.height));
      const interBananaPadding = 5; // Padding between bananas
      const borderPadding = 3;      // Padding around the entire composition
  
      // Create final composition with all 8 bananas plus padding
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = maxWidth * 8 + interBananaPadding * 7 + borderPadding * 2;
      finalCanvas.height = maxHeight + borderPadding * 2;
  
      const finalCtx = finalCanvas.getContext('2d', { alpha: true })!;
      // Clear any background
      finalCtx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
  
      // Place all bananas with padding
      bananas.forEach((banana, index) => {
        const x = borderPadding + index * (maxWidth + interBananaPadding);
        const y = borderPadding + (maxHeight - banana.height) / 2;
        finalCtx.drawImage(banana, x, y);
      });
  
      // Export
      const exportFilename = formatExportFilename(
        fileName,
        'BASKETBALL_BANANAS',
        FILE_FORMATS.PNG
      );
  
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to create blob');
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = exportFilename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Failed to export bananas:', error);
    }
  };
  
  

  const handleHalfRightClick = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
      if (!canvas) {
        console.error('Canvas not found');
        reject(new Error('Canvas not found'));
        return;
      }
  
      try {
        const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
        if (!gl) {
          throw new Error('WebGL context not found');
        }
  
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
  
        const width = canvas.width;
        const height = canvas.height;
        const halfWidth = Math.floor(width / 2);
        
        if (halfWidth === 0 || height === 0) {
          throw new Error('Invalid canvas dimensions');
        }
  
        // Read pixels from the right half instead of left half
        const pixels = new Uint8Array(halfWidth * height * 4);
        gl.readPixels(halfWidth, 0, halfWidth, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  
        const offscreen = document.createElement('canvas');
        offscreen.width = halfWidth;
        offscreen.height = height;
        const ctx = offscreen.getContext('2d');
  
        if (!ctx) {
          throw new Error('Failed to get 2D context');
        }
  
        const imageData = new ImageData(new Uint8ClampedArray(pixels), halfWidth, height);
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = halfWidth;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        
        if (!tempCtx) {
          throw new Error('Failed to get temp context');
        }
  
        tempCtx.putImageData(imageData, 0, 0);
        ctx.translate(0, height);
        ctx.scale(1, -1);
        ctx.drawImage(tempCanvas, 0, 0);
  
        // Use the existing format for the filename
        const exportFilename = formatExportFilename(
          fileName,
          'FLOWER_RIGHT',
          FILE_FORMATS.PNG
        );
  
        offscreen.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
  
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = exportFilename;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            resolve();
          },
          `image/${FILE_FORMATS.PNG}`
        );
      } catch (error) {
        reject(error);
      }
    });
  };
  
  const handlePeanutsClick = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const canvas = document.querySelector('#projectionCanvas') as HTMLCanvasElement;
      if (!canvas) {
        console.error('Canvas not found');
        reject(new Error('Canvas not found'));
        return;
      }
  
      try {
        const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });
        if (!gl) {
          throw new Error('WebGL context not found');
        }
  
        // Render the projection onto the canvas
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
  
        // We assume the projection contains two baseball shapes (peanuts) stacked vertically.
        // Each peanut is in the center of a 2:1 rectangle.
        // We will split the canvas into a top half and a bottom half, crop each to its non-transparent
        // content (with a 5px margin), and then recompose the two into a final image with a 5px gap in between
        // and a 5px border around the overall image.
  
        const fullWidth = canvas.width;
        const fullHeight = canvas.height;
        const halfHeight = Math.floor(fullHeight / 2);
        const margin = 5; // 5px buffer
  
        // Create separate canvases for the top and bottom halves.
        const topCanvas = document.createElement('canvas');
        topCanvas.width = fullWidth;
        topCanvas.height = halfHeight;
        const topCtx = topCanvas.getContext('2d', { alpha: true })!;
        topCtx.drawImage(canvas, 0, 0, fullWidth, halfHeight, 0, 0, fullWidth, halfHeight);
  
        const bottomCanvas = document.createElement('canvas');
        bottomCanvas.width = fullWidth;
        bottomCanvas.height = halfHeight;
        const bottomCtx = bottomCanvas.getContext('2d', { alpha: true })!;
        bottomCtx.drawImage(
          canvas,
          0,
          halfHeight,
          fullWidth,
          halfHeight,
          0,
          0,
          fullWidth,
          halfHeight
        );
  
        // Utility function to compute the bounding box of non-transparent pixels.
        const getContentBounds = (imageData: ImageData) => {
          const { width, height, data } = imageData;
          let minX = width,
            minY = height,
            maxX = 0,
            maxY = 0;
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const i = (y * width + x) * 4;
              const alpha = data[i + 3];
              if (alpha > 0) {
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
              }
            }
          }
          return {
            left: minX,
            top: minY,
            width: maxX - minX + 1,
            height: maxY - minY + 1,
          };
        };
  
        // Get the non-transparent bounds for each half.
        const topImageData = topCtx.getImageData(0, 0, fullWidth, halfHeight);
        const topBounds = getContentBounds(topImageData);
        const bottomImageData = bottomCtx.getImageData(0, 0, fullWidth, halfHeight);
        const bottomBounds = getContentBounds(bottomImageData);
  
        // Expand each bounding box by our margin (making sure not to exceed the canvas boundaries)
        const topCropX = Math.max(topBounds.left - margin, 0);
        const topCropY = Math.max(topBounds.top - margin, 0);
        const topCropWidth = Math.min(topBounds.width + 2 * margin, fullWidth - topCropX);
        const topCropHeight = Math.min(topBounds.height + 2 * margin, halfHeight - topCropY);
  
        const bottomCropX = Math.max(bottomBounds.left - margin, 0);
        const bottomCropY = Math.max(bottomBounds.top - margin, 0);
        const bottomCropWidth = Math.min(bottomBounds.width + 2 * margin, fullWidth - bottomCropX);
        const bottomCropHeight = Math.min(bottomBounds.height + 2 * margin, halfHeight - bottomCropY);
  
        // Create new canvases for the cropped peanuts.
        const croppedTopCanvas = document.createElement('canvas');
        croppedTopCanvas.width = topCropWidth;
        croppedTopCanvas.height = topCropHeight;
        const croppedTopCtx = croppedTopCanvas.getContext('2d', { alpha: true })!;
        croppedTopCtx.drawImage(
          topCanvas,
          topCropX,
          topCropY,
          topCropWidth,
          topCropHeight,
          0,
          0,
          topCropWidth,
          topCropHeight
        );
  
        const croppedBottomCanvas = document.createElement('canvas');
        croppedBottomCanvas.width = bottomCropWidth;
        croppedBottomCanvas.height = bottomCropHeight;
        const croppedBottomCtx = croppedBottomCanvas.getContext('2d', { alpha: true })!;
        croppedBottomCtx.drawImage(
          bottomCanvas,
          bottomCropX,
          bottomCropY,
          bottomCropWidth,
          bottomCropHeight,
          0,
          0,
          bottomCropWidth,
          bottomCropHeight
        );
  
        // Compose the final image.
        // We want a 5px border on all sides and a 5px gap between the two peanuts.
        // Determine the final image dimensions.
        const finalContentWidth = Math.max(croppedTopCanvas.width, croppedBottomCanvas.width);
        const finalWidth = finalContentWidth + 2 * margin;
        const finalHeight =
          croppedTopCanvas.height + croppedBottomCanvas.height + margin /* gap */ + 2 * margin;
  
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = finalWidth;
        finalCanvas.height = finalHeight;
        const finalCtx = finalCanvas.getContext('2d', { alpha: true })!;
        finalCtx.clearRect(0, 0, finalWidth, finalHeight);
  
        // Draw the top peanut, centered horizontally within the final image.
        const topX = margin + (finalContentWidth - croppedTopCanvas.width) / 2;
        const topY = margin;
        finalCtx.drawImage(croppedTopCanvas, topX, topY);
  
        // Draw the bottom peanut, centered horizontally and positioned with a gap below the top one.
        const bottomX = margin + (finalContentWidth - croppedBottomCanvas.width) / 2;
        const bottomY = margin + croppedTopCanvas.height + margin;
        finalCtx.drawImage(croppedBottomCanvas, bottomX, bottomY);
  
        // Export the final composed image.
        const exportFilename = formatExportFilename(
          fileName,
          'BASEBALL_PEANUTS',
          FILE_FORMATS.PNG
        );
  
        finalCanvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = exportFilename;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      } catch (error) {
        console.error('Failed to export peanuts:', error);
        reject(error);
      }
    });
  };
  
  
  return (
    <div className="App" style={{ 
      display: 'flex', 
      height: '100vh', 
      padding: '20px', 
      boxSizing: 'border-box', 
      alignItems: 'flex-start',
      backgroundColor: '#1A1A1A',
      color: '#CCCCCC'
    }}>
      <div style={{ 
        width: '300px', 
        marginRight: '20px', 
        display: 'flex', 
        flexDirection: 'column', 
        padding: '20px', 
        boxShadow: '0 0 10px rgba(0,0,0,0.3)', 
        borderRadius: '10px',
        backgroundColor: '#222222',
        color: '#CCCCCC'
      }}>
        {/* Hidden file input */}
        <input 
          id="imageUpload" 
          type="file" 
          accept="image/*" 
          style={{ display: 'none' }} 
          onChange={(e) => e.target.files && handleImageUpload(e.target.files[0])} 
        />

        {/* Sphere preview */}
        {inputImage && (
          <div style={{ width: '100%', aspectRatio: '1/1', marginBottom: '4px', border: 'none' }}>
            <SpherePreviewer 
              texture={inputImage}
              tilt={tilt}
              isRotating={isRotating}
              offset={offset}
              rotation={rotation}
              glContext={null}
              sharedTexture={null}
              ref={spherePreviewerRef}
            />
          </div>
        )}

         {/* Tilt Control */}
         <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
          <EditableLabel
            value={tilt}
            onChange={(value) => setTilt(value)}
            min={-90}
            max={90}
            label="Sphere"
            unit="°"
            showPlayPause={true}
            isRotating={isRotating}
            onRotationToggle={() => setIsRotating(!isRotating)}
          />
          <input
            type="range"
            min="-90"
            max="90"
            value={tilt}
            onChange={(e) => handleInputChangeWithSnap(setTilt, Number(e.target.value), 90, 'tilt', [0])}
          />
        </label>

        {/* Control Bar */}
        <ControlBar
          onUpload={() => document.getElementById('imageUpload')?.click()}
          onReset={resetProjectionControls}
          onExportGLB={handleGlbClick}
          onExportGLTF={handleGltfClick}
          onExportWEBM={handleWebmClick}
          onExportJPG={handleJpgClick}
          onExportPNG={handlePngClick}
          onExportHalfPNG={handleHalfPngClick}
          onExportRightHalf={handleHalfRightClick}
          onExportWEBP={handleWebpClick}
          onExportSpherePNG={handleSphereSnapshot}  // This prop name will be updated next
          onExportPeel={handlePeelClick}   // Add this line
          onExportBananas={handleBananasClick}
          onExportPeanuts={handlePeanutsClick}
          hasImage={!!inputImage}
          selectedProjection={selectedProjection}
          onProjectionChange={setSelectedProjection}
          projectionTypes={PROJECTION_TYPES}
          isExportingVideo={isExportingVideo}
          fileName={fileName}
          fileSize={fileSize}
          imageResolution={imageResolution}
        />

        {/* Offset Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={offset}
            onChange={(value) => setOffset(value)}
            min={-imageWidth}
            max={imageWidth}
            label="Offset"
            unit="px"
          />
          <input
            type="range"
            min={imageWidth === 0 ? -1000 : -imageWidth}
            max={imageWidth === 0 ? 1000 : imageWidth}
            value={offset}
            onChange={(e) => handleInputChangeWithSnap(setOffset, Number(e.target.value), imageWidth, 'offset', [0])}
          />
        </label>

        {/* Longitude Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={longitude}
            onChange={(value) => setLongitude(value)}
            min={-180}
            max={180}
            label="Longitude"
            unit="°"
          />
          <input
            type="range"
            min={-MAX_LONGITUDE}
            max={MAX_LONGITUDE}
            value={longitude}
            onChange={(e) => handleInputChangeWithSnap(setLongitude, Number(e.target.value), MAX_LONGITUDE, 'longitude', [0, -90, 90])}
          />
        </label>

        {/* Latitude Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={latitude}
            onChange={(value) => setLatitude(value)}
            min={-90}
            max={90}
            label="Latitude"
            unit="°"
          />
          <input
            type="range"
            min={-MAX_LATITUDE}
            max={MAX_LATITUDE}
            value={latitude}
            onChange={(e) => handleInputChangeWithSnap(setLatitude, Number(e.target.value), MAX_LATITUDE, 'latitude', [0, -45, 45])}
          />
        </label>

        {/* Spin Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={spin}
            onChange={(value) => setSpin(value)}
            min={-180}
            max={180}
            label="Spin"
            unit="°"
          />
          <input
            type="range"
            min="-180"
            max="180"
            value={spin}
            onChange={(e) => handleInputChangeWithSnap(setSpin, Number(e.target.value), 180, 'spin', [0, -90, 90])}
          />
        </label>

        {/* Pinch Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={pinch}
            onChange={(value) => setPinch(value)}
            min={-100}
            max={100}
            label="Pinch"
            unit="%"
          />
          <input
            type="range"
            min="-100"
            max="100"
            value={pinch}
            onChange={(e) => handleInputChangeWithSnap(setPinch, Number(e.target.value), 100, 'pinch', [0])}
          />
        </label>

        {/* Trim Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={trim}
            onChange={(value) => setTrim(value)}
            min={-100}
            max={100}
            label="Trim"
            unit="%"
          />
          <input
            type="range"
            min="-100"
            max="100"
            value={trim}
            onChange={(e) => handleInputChangeWithSnap(setTrim, Number(e.target.value), 100, 'trim', [0])}
          />
        </label>

       {/* Seam Control */}
       <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={surplusX}
            onChange={(value) => setSurplusX(value)}
            min={0}
            max={4}
            label="Seam"
            unit="°"
          />
          <input
            type="range"
            min="0"
            max="4"
            step="0.1"
            value={surplusX}
            onChange={(e) => setSurplusX(Number(e.target.value))}
            disabled={!(selectedProjection.toLowerCase() === 'pillow' || 
                       selectedProjection.toLowerCase() === 'flower' ||
                       selectedProjection.toLowerCase() === 'tennis' ||
                       selectedProjection.toLowerCase() === 'baseball')}
          />
        </label>

        {/* Petals Control */}
        <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
          <EditableLabel
            value={petals}
            onChange={(value) => setPetals(value)}
            min={1}
            max={16}
            label="Petals"
            unit=""
          />
          <input
            type="range"
            min="4"
            max="16"
            value={petals}
            onChange={(e) => setPetals(Number(e.target.value))}
            disabled={!(selectedProjection.toLowerCase() === 'pillow' || selectedProjection.toLowerCase() === 'flower')}
          />
        </label>
      </div>

      {/* Right Panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 40px)' }}>
        {!inputImage ? (
          <ImageUploader onImageUpload={handleImageUpload} />
        ) : (
          <ProjectionPreviewer
            texture={inputImage}
            offset={offset}
            longitude={longitude}
            latitude={latitude}
            spin={spin}
            projectionType={PROJECTION_TYPES[selectedProjection]}
            pinch={pinch}
            trim={trim}
            petals={selectedProjection.toLowerCase() === 'basketball' ? 1 : petals}
            setPetals={setPetals}
            beeLanding={beeLanding}
            setBeeLanding={setBeeLanding}
            surplusY={surplusY}
            setSurplusY={setSurplusY}
            surplusX={surplusX}
            setSurplusX={setSurplusX}
            startSurplusX={DEFAULT_START_SURPLUSX}
            endSurplusX={DEFAULT_END_SURPLUSX}
            startSurplusY={DEFAULT_START_SURPLUSY}
            endSurplusY={DEFAULT_END_SURPLUSY}
            meridian={meridian}
            setMeridian={setMeridian}
          />
        )}
      </div>
      <ConsoleLogger />
    </div>
  );
};

export default App;